import { inServices, ladderAccess, useServicesSections } from '@church/data'
import { Link, Outlet, createFileRoute, redirect, useLocation } from '@tanstack/react-router'
import { PageHeader } from '../components/ui.tsx'
import { currentSite } from '../lib/site.ts'

/**
 * Services' sections — Plans, Songs, People, Schedule, Sermons — each at its own
 * address. In ForChurch they sit under one row of tabs (links: Back, reload
 * and a shared link all land on the section); in ForServices the menu lists
 * them, so there is no row. The row is its own part of a view transition: it
 * stays still while the section under it changes.
 */
export const Route = createFileRoute('/_authed/_shell/services/_sections')({
  beforeLoad: ({ context }) => {
    // Anyone with a place in ForServices; each section hides what is not theirs.
    if (!inServices(ladderAccess(context.me)))
      // In ForServices, '/' is Plans: their own schedule instead.
      throw redirect({ to: currentSite() === 'services' ? '/my-schedule' : '/' })
  },
  component: ServicesSections,
})

const SECTIONS = [
  {
    id: 'plans',
    label: 'Plans',
    to: '/services/plans',
    under: ['/services/plans', '/services/types'],
  },
  { id: 'songs', label: 'Songs', to: '/services/songs', under: ['/services/songs'] },
  { id: 'people', label: 'People', to: '/services/people', under: ['/services/people'] },
  { id: 'schedule', label: 'Schedule', to: '/services/schedule', under: ['/services/schedule'] },
  { id: 'sermons', label: 'Sermons', to: '/services/sermons', under: ['/services/sermons'] },
] as const

function ServicesSections() {
  const { pathname } = useLocation()
  // Only the sections their access reaches (docs/13-ACCESS.md); the address still opens any.
  const shown = useServicesSections()
  const current = SECTIONS.find((s) => s.under.some((p) => pathname.startsWith(p)))
  const tabs = SECTIONS.filter((section) => shown[section.id] || section === current)
  // One song, one service type's plans, one person: a screen within a
  // section, where a phone keeps the height for it (the breadcrumbs lead back).
  const within = /^\/services\/(songs\/(?!discover\b)[^/]+|types\/|people\/[^/]+)/.test(pathname)

  if (currentSite() === 'services')
    return (
      <div className="space-y-6">
        <PageHeader title={current?.label ?? 'Services'} />
        <Outlet />
      </div>
    )
  // One section is nothing to choose between: no row.
  if (tabs.length < 2)
    return (
      <div className="space-y-6">
        <PageHeader title="Services" />
        <Outlet />
      </div>
    )
  return (
    <div className="space-y-6">
      <PageHeader title="Services" />
      <nav
        aria-label="Services"
        className={`vt-services-sections -mx-gutter border-b border-border-subtle sm:mx-0 ${
          within ? 'hidden sm:block' : ''
        }`}
      >
        <ul className="flex gap-1 overflow-x-auto px-gutter no-scrollbar sm:px-0">
          {tabs.map((section) => {
            const active = section === current
            return (
              <li key={section.to} className="shrink-0">
                <Link
                  to={section.to}
                  aria-current={active ? 'page' : undefined}
                  className={`relative flex min-h-11 items-center px-3 text-sm font-medium whitespace-nowrap no-underline transition-colors duration-(--duration-fast) sm:px-4 ${
                    active
                      ? 'text-content-brand after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full after:bg-brand-solid'
                      : 'text-content-muted hover:text-content-default'
                  }`}
                >
                  {section.label}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
      <Outlet />
    </div>
  )
}
